import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ConfirmationModal, ConfirmationModalProps } from '@/components/ui/confirmation-modal';
import { InputModal, InputModalProps } from '@/components/ui/input-modal';

interface ConfirmModalOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive' | 'warning' | 'success';
}

interface InputModalOptions {
  title: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  inputType?: 'text' | 'email' | 'password' | 'textarea';
  confirmText?: string;
  cancelText?: string;
  required?: boolean;
  validation?: (value: string) => string | null;
}

interface ModalContextType {
  showConfirmModal: (options: ConfirmModalOptions) => Promise<boolean>;
  showInputModal: (options: InputModalOptions) => Promise<string | null>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    props: Partial<ConfirmationModalProps>;
    resolve: (value: boolean) => void;
  } | null>(null);

  const [inputModal, setInputModal] = useState<{
    open: boolean;
    props: Partial<InputModalProps>;
    resolve: (value: string | null) => void;
  } | null>(null);

  const showConfirmModal = (options: ConfirmModalOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmModal({
        open: true,
        props: {
          title: options.title,
          description: options.description,
          confirmText: options.confirmText,
          cancelText: options.cancelText,
          variant: options.variant,
          onConfirm: () => {
            setConfirmModal(null);
            resolve(true);
          },
          onCancel: () => {
            setConfirmModal(null);
            resolve(false);
          },
        },
        resolve,
      });
    });
  };

  const showInputModal = (options: InputModalOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setInputModal({
        open: true,
        props: {
          title: options.title,
          description: options.description,
          placeholder: options.placeholder,
          defaultValue: options.defaultValue,
          inputType: options.inputType,
          confirmText: options.confirmText,
          cancelText: options.cancelText,
          required: options.required,
          validation: options.validation,
          onConfirm: (value: string) => {
            setInputModal(null);
            resolve(value);
          },
          onCancel: () => {
            setInputModal(null);
            resolve(null);
          },
        },
        resolve,
      });
    });
  };

  const handleConfirmModalOpenChange = (open: boolean) => {
    if (!open && confirmModal) {
      confirmModal.resolve(false);
      setConfirmModal(null);
    }
  };

  const handleInputModalOpenChange = (open: boolean) => {
    if (!open && inputModal) {
      inputModal.resolve(null);
      setInputModal(null);
    }
  };

  return (
    <ModalContext.Provider value={{ showConfirmModal, showInputModal }}>
      {children}
      
      {confirmModal && (
        <ConfirmationModal
          open={confirmModal.open}
          onOpenChange={handleConfirmModalOpenChange}
          title={confirmModal.props.title || ''}
          description={confirmModal.props.description || ''}
          confirmText={confirmModal.props.confirmText}
          cancelText={confirmModal.props.cancelText}
          variant={confirmModal.props.variant}
          onConfirm={confirmModal.props.onConfirm || (() => {})}
          onCancel={confirmModal.props.onCancel}
        />
      )}

      {inputModal && (
        <InputModal
          open={inputModal.open}
          onOpenChange={handleInputModalOpenChange}
          title={inputModal.props.title || ''}
          description={inputModal.props.description}
          placeholder={inputModal.props.placeholder}
          defaultValue={inputModal.props.defaultValue}
          inputType={inputModal.props.inputType}
          confirmText={inputModal.props.confirmText}
          cancelText={inputModal.props.cancelText}
          required={inputModal.props.required}
          validation={inputModal.props.validation}
          onConfirm={inputModal.props.onConfirm || (() => {})}
          onCancel={inputModal.props.onCancel}
        />
      )}
    </ModalContext.Provider>
  );
}
